import { DTOgetOrderDetailedData, PAYMENT_TYPES, PAYMENTS_PROVIDER, TYPES_OF_BILLING } from 'types/orders';
import { SPECIAL_BUSINESS_NAME_TYPES_ID } from 'utils/productDefinitions';
import { IlogisticServiceDetails } from 'types/services';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import { calcStatusForLogisticService, calcStatusForOrder, getBillingTypePrefix } from './utils';

const EXTERNAL_BILLING_PROVIDERS = [
  TYPES_OF_BILLING.GIGSTACK,
  TYPES_OF_BILLING.STRIPE_USA_NUDOS_LLC,
  TYPES_OF_BILLING.STRIPE_USA_NUDOS_CORP
];

export const adapterPaymentDataFromAPI = (orderData?: DTOgetOrderDetailedData) => {
  if (!orderData) return;
  try {
    const isAutomatedPayment = orderData?.generalData?.paymentData?.wasPaymentAutomationAttempted;

    const status = calcStatusForOrder(orderData);

    const billingTypePrefix = getBillingTypePrefix({ billingProvider: orderData.billingMethod?.billingProvider });

    const specialBusinessType =
      orderData?.billingData?.specialBusinessNameTypeId === SPECIAL_BUSINESS_NAME_TYPES_ID?.TRADING;

    const displayCountryBilling =
      !specialBusinessType &&
      !billingTypePrefix &&
      orderData?.billingData?.countryBilling &&
      orderData?.generalData?.paymentData?.currency === 'USD';

    const basePaymentMethod =
      orderData?.generalData?.paymentData?.paymentMethod === PAYMENT_TYPES.INTERNATIONAL_CARD
        ? 'Tarjeta de crédito USD'
        : orderData?.generalData?.paymentData?.paymentMethod ?? '';

    const paymentMethod = basePaymentMethod
      .replace('Tarjeta crédito', 'Tarjeta de crédito')
      .replace('Crédito Bord', 'Crédito .bord en ')
      .replace('Transf.', 'Transferencia');

    const showCurrency = basePaymentMethod === PAYMENT_TYPES.CARD_MXN;

    const finalPrefix = displayCountryBilling ? orderData?.billingData?.countryBilling : billingTypePrefix;

    const prefixDecorated = finalPrefix
      ? `- ${finalPrefix}`
      : showCurrency && orderData?.generalData?.paymentData?.currency
      ? `${orderData?.generalData?.paymentData?.currency} `
      : '';

    return {
      status,
      method: orderData?.generalData?.paymentData?.paymentMethod ? `${paymentMethod} ${prefixDecorated}` : undefined,
      paymentType: isAutomatedPayment ? 'automatizado' : 'manual',
      voucherUrl: orderData?.generalData?.paymentData?.proofOfPayment ?? '',
      committedPaymentDate: orderData?.generalData?.paymentData?.committedPaymentDate,
      paymentLink: orderData?.generalData?.paymentData?.paymentLink,
      paymentAgreementDays: orderData?.generalData?.paymentData?.paymentAgreementDays,
      paymentData: {
        bankAccountInfo: orderData?.generalData?.bankAccountInfo,
        ...orderData?.generalData?.paymentData
      }
    };
  } catch (error) {
    displayErrorNotification();
  }
};

export const adapterServiceLogisticPaymentDataFromAPI = (logisticServiceData: IlogisticServiceDetails) => {
  try {
    const {
      paymentLink,
      proofOfPaymentUrl,
      billingCountry,
      paymentMethod,
      hasActiveCredit,
      purchaseOrder,
      paymentAgreementDate
    } = logisticServiceData;

    const status = calcStatusForLogisticService(logisticServiceData);

    const billingProvider = logisticServiceData.billingMethod?.billingProvider;

    const paymentAutomatizated =
      billingProvider &&
      !purchaseOrder &&
      !paymentAgreementDate &&
      EXTERNAL_BILLING_PROVIDERS.includes(billingProvider);

    const isManualPayment = !paymentAutomatizated || hasActiveCredit;

    const wasPaidThroughStripe = proofOfPaymentUrl === PAYMENTS_PROVIDER;
    const proofOfPaymentLink = wasPaidThroughStripe ? paymentLink : proofOfPaymentUrl;

    const billingTypePrefixRaw = getBillingTypePrefix({
      billingCountryName: billingCountry?.name,
      billingProvider,
      paymentMethod
    });

    const billingTypePrefix = billingTypePrefixRaw ? `- ${billingTypePrefixRaw}` : '';

    const baseLabel =
      paymentMethod === PAYMENT_TYPES.INTERNATIONAL_CARD ? 'Tarjeta de crédito USD' : paymentMethod ?? '';

    const paymentMethodLabel = baseLabel
      .replace('Tarjeta crédito', 'Tarjeta de crédito')
      .replace('Crédito Bord', 'Crédito .bord en ')
      .replace('Transf.', 'Transferencia');

    return {
      status,
      method: paymentMethodLabel ? `${paymentMethodLabel} ${billingTypePrefix}` : '',
      paymentType: isManualPayment ? 'manual' : 'automatizado',
      voucherUrl: proofOfPaymentLink ?? '',
      committedPaymentDate: paymentAgreementDate,
      paymentAgreementDays: logisticServiceData?.paymentAgreementDays,
      paymentLink,
      paymentData: logisticServiceData
    };
  } catch (error) {
    displayErrorNotification();
  }
};
